import { useState, useEffect } from "react";
import {
  Database,
  Eye,
  Loader2,
  RefreshCw,
  Table2,
  Layers,
  Search,
  Building2,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/services/api";

export default function DataPreview() {
  const [bigqueryConnections, setBigqueryConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState("");
  const [selectedDataset, setSelectedDataset] = useState("datalake_gold");
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tablesLoading, setTablesLoading] = useState(false);
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedTable, setSelectedTable] = useState("");
  
  // Search
  const [searchTerm, setSearchTerm] = useState("");

  const datasets = [
    { id: "datalake_gold", name: "Gold Layer (Analytics)", icon: "🥇" },
    { id: "datalake_silver", name: "Silver Layer (Cleaned)", icon: "🥈" },
    { id: "bronze_layer", name: "Bronze Layer (Raw)", icon: "🥉" },
  ];

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    if (selectedConnection && selectedDataset) {
      fetchTables();
    }
  }, [selectedConnection, selectedDataset]);

  const fetchConnections = async () => {
    try {
      const bqData = await api.getBigQueryConnections();
      setBigqueryConnections(bqData.filter(c => c.status === "connected"));
      
      if (bqData.length > 0) {
        const connected = bqData.find(c => c.status === "connected");
        if (connected) {
          setSelectedConnection(connected.id);
        }
      }
    } catch (error) {
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    setTablesLoading(true);
    try {
      // Use the RLS gold-tables endpoint for gold layer (includes tenant_id info)
      if (selectedDataset === "datalake_gold") {
        const data = await api.getGoldTablesForRLS(selectedConnection);
        setTables(data.tables || []);
      } else {
        // For other layers, use standard list
        const data = await api.listBigQueryTables(selectedConnection, selectedDataset);
        setTables(data.tables?.map(t => ({ table_name: t, num_rows: null, has_tenant_id: null })) || []);
      }
    } catch (error) {
      console.error("Failed to load tables:", error);
      setTables([]);
    } finally {
      setTablesLoading(false);
    }
  };

  const handlePreview = async (tableName) => {
    setSelectedTable(tableName);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const data = await api.previewBigQueryTable(selectedConnection, selectedDataset, tableName, 50);
      setPreviewData(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load preview");
      setPreviewData({ error: true, message: error.response?.data?.detail || "Failed to load data" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredTables = tables.filter(t => 
    t.table_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-preview-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Eye className="w-5 h-5 text-blue-600" />
            Data Preview
          </h2>
          <p className="text-sm text-muted-foreground">
            Preview data in BigQuery tables across all layers
          </p>
        </div>
        <Button variant="outline" onClick={fetchTables} size="sm">
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-end gap-4">
            <div className="space-y-2 flex-1">
              <Label>BigQuery Connection</Label>
              <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select connection" />
                </SelectTrigger>
                <SelectContent>
                  {bigqueryConnections.map((conn) => (
                    <SelectItem key={conn.id} value={conn.id}>
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4" />
                        {conn.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2 flex-1">
              <Label>Dataset / Layer</Label>
              <Select value={selectedDataset} onValueChange={setSelectedDataset}>
                <SelectTrigger>
                  <SelectValue placeholder="Select dataset" />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map((ds) => (
                    <SelectItem key={ds.id} value={ds.id}>
                      <div className="flex items-center gap-2">
                        <span>{ds.icon}</span>
                        {ds.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 flex-1">
              <Label>Search Tables</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  placeholder="Filter tables..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tables List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Table2 className="w-4 h-4" />
            Tables in {datasets.find(d => d.id === selectedDataset)?.name || selectedDataset}
          </CardTitle>
          <CardDescription>
            {filteredTables.length} table{filteredTables.length !== 1 ? "s" : ""} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tablesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTables.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Table2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No tables found in this dataset</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Table Name</TableHead>
                  <TableHead>Rows</TableHead>
                  {selectedDataset === "datalake_gold" && (
                    <>
                      <TableHead>Has tenant_id</TableHead>
                      <TableHead>RLS</TableHead>
                    </>
                  )}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTables.map((table) => (
                  <TableRow key={table.table_name}>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-muted-foreground" />
                        {table.table_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      {table.num_rows != null ? table.num_rows.toLocaleString() : "—"}
                    </TableCell>
                    {selectedDataset === "datalake_gold" && (
                      <>
                        <TableCell>
                          {table.has_tenant_id ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <Building2 className="w-3 h-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-50 text-gray-500">
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {table.has_tenant_id && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              <Shield className="w-3 h-3 mr-1" />
                              Protected
                            </Badge>
                          )}
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-right">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handlePreview(table.table_name)}
                        data-testid={`preview-${table.table_name}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Preview: {selectedDataset}.{selectedTable}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : previewData?.error ? (
              <div className="text-center py-12">
                <p className="text-red-500">{previewData.message}</p>
              </div>
            ) : previewData?.rows?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Table2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No data found</p>
                {previewData?.has_rls && (
                  <p className="text-sm mt-2 text-purple-600">
                    <Shield className="w-4 h-4 inline mr-1" />
                    This table has Row-Level Security. Data is filtered based on your permissions.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stats */}
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span>Total rows: {previewData?.total_rows?.toLocaleString() || "—"}</span>
                  <span>Columns: {previewData?.columns?.length || 0}</span>
                  <span>Showing: {previewData?.rows?.length || 0} rows</span>
                  {previewData?.has_rls && (
                    <Badge variant="outline" className="bg-purple-50 text-purple-700">
                      <Shield className="w-3 h-3 mr-1" />
                      RLS Active
                    </Badge>
                  )}
                </div>
                
                {/* Data Table */}
                <div className="border rounded-lg overflow-auto max-h-[50vh]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {previewData?.columns?.map((col, idx) => (
                          <TableHead key={idx} className="whitespace-nowrap bg-muted/50">
                            <div className="flex flex-col">
                              <span className="font-semibold">{col.name}</span>
                              <span className="text-xs text-muted-foreground font-normal">{col.type}</span>
                            </div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData?.rows?.map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          {previewData?.columns?.map((col, colIdx) => (
                            <TableCell key={colIdx} className="font-mono text-xs whitespace-nowrap">
                              {row[col.name] === null ? (
                                <span className="text-muted-foreground italic">null</span>
                              ) : (
                                String(row[col.name]).substring(0, 100)
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
